import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import OverlaySpinner from '@components/OverlaySpinner';
import ScrollToTopOnMount from '@components/ScrollToTopOnMount';
import { STATUS_CODE } from '@shared/constants';
import { Col, Row } from '@components/Grid';
import useIsDesktopLayout from '@hooks/useIsDesktopLayout';
import ModalPage from '@components/ModalPage';

import Main from './Main';
import Error from '../Error/Loadable';
import StyledGrid from './components/StyledGrid';

function Shell({
  children,
  leftColumn,
  rightColumn,
  header,
  footer,
  notification,
  showOverlaySpinner,
  isShowingErrorPage,
  isModal,
  errorCode,
  scrollToTopOnMount,
  updateIsDesktopLayout,
  fullScreen,
  onClose,
  goBack,
}) {
  const isDesktop = useIsDesktopLayout();
  useEffect(() => updateIsDesktopLayout(isDesktop), [
    isDesktop,
    updateIsDesktopLayout,
  ]);

  if (isShowingErrorPage) return <Error errorCode={errorCode} />;

  const scrollToTop = scrollToTopOnMount || isModal;

  return isDesktop && !fullScreen ? (
    <StyledGrid withBackground={isModal} fullHeight flexDirection="column">
      <Row>{!isModal && header}</Row>
      <Row fullSize>
        <Col size={1} collapse="lg">
          {leftColumn}
        </Col>
        <Col size={2}>
          {showOverlaySpinner && <OverlaySpinner />}
          <>
            {isModal ? (
              <ModalPage
                open={!showOverlaySpinner}
                header={header}
                footer={footer}
                onClose={onClose || goBack}
              >
                <Main notification={notification}>{children}</Main>
              </ModalPage>
            ) : (
              <Main notification={notification}>{children}</Main>
            )}
          </>
        </Col>
        <Col size={1} collapse="lg">
          {rightColumn}
        </Col>
      </Row>
      <Row>{!isModal && footer}</Row>
    </StyledGrid>
  ) : (
    <>
      {scrollToTop && <ScrollToTopOnMount />}

      <Main notification={notification}>
        {header}
        {children}
        {footer}
      </Main>

      {showOverlaySpinner && <OverlaySpinner />}
    </>
  );
}

Shell.propTypes = {
  // data
  children: PropTypes.node.isRequired,
  leftColumn: PropTypes.node,
  rightColumn: PropTypes.node,
  header: PropTypes.node,
  footer: PropTypes.node,
  fullScreen: PropTypes.bool,
  notification: PropTypes.shape({
    title: PropTypes.string,
    show: PropTypes.bool,
    color: PropTypes.string,
  }).isRequired,
  showOverlaySpinner: PropTypes.bool,
  errorCode: PropTypes.number,
  isModal: PropTypes.bool,
  isShowingErrorPage: PropTypes.bool,
  scrollToTopOnMount: PropTypes.bool,
  // callbacks
  updateIsDesktopLayout: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};

Shell.defaultProps = {
  isModal: false,
  isShowingErrorPage: false,
  showOverlaySpinner: false,
  errorCode: STATUS_CODE.INTERNAL_ERROR,
  scrollToTopOnMount: false,
  header: null,
  leftColumn: null,
  rightColumn: null,
  footer: null,
  fullScreen: false,
};

export default Shell;
